import { DeviceChangeBadge } from 'Modules/softphone/components/SoftphoneDevTools/DeviceChangeBadge';
import type { DeviceChangeLanesProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugRecord/DebugRecordDevices/DeviceChangeLanes/index.types';
import {
  DEVICE,
  MICRO_LABEL,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import {
  buildDeviceLanes,
  formatRelativeLabel,
  formatTimestamp,
} from 'Modules/softphone/components/SoftphoneDevTools/utils';
import type {
  DeviceChangeType,
  SoftphoneEvent,
} from 'Modules/softphone/services/EventService';
import React, { useMemo, useState } from 'react';
import { cn } from '~/lib/utils';

/** `details` is an open bag, so a label is only a label once it proves to be a string. */
const readLabel = (
  event: SoftphoneEvent,
  key: 'fromLabel' | 'toLabel'
): string | null => {
  const value: unknown = event.details?.[key];

  return typeof value === 'string' ? value : null;
};

interface SelectedChange {
  type: DeviceChangeType;
  event: SoftphoneEvent;
}

/**
 * DeviceChangeLanes - The call's device switches on one real-time axis
 *
 * The list answers "what changed"; the lanes answer "when, and how close
 * together". Four mic switches inside three seconds and four spread across the
 * call read identically as rows, and only the first is a device flapping — so
 * the marks are placed on elapsed time and a cluster is left to overlap.
 */
export const DeviceChangeLanes: React.FC<DeviceChangeLanesProps> = ({
  changes,
  startTime,
  endTime,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const model = useMemo(
    () => buildDeviceLanes(changes, { startTime, endTime, nowMs: Date.now() }),
    [changes, startTime, endTime]
  );

  /* Something is always open, so the panel reads without a click first. The
     newest change is the one a report is usually about. */
  const selected = useMemo<SelectedChange | null>(() => {
    const all = model.lanes.flatMap((lane) =>
      lane.marks.map((mark) => ({ type: lane.type, event: mark.event }))
    );

    return (
      all.find((entry) => entry.event.id === selectedId) ??
      all.reduce<SelectedChange | null>(
        (latest, entry) =>
          !latest || entry.event.timestamp > latest.event.timestamp
            ? entry
            : latest,
        null
      )
    );
  }, [model, selectedId]);

  const selectedStyle = selected ? DEVICE[selected.type] : null;

  return (
    <div className="flex flex-col gap-2.5 px-3 py-2.5">
      <div role="group" aria-label="Device change lanes">
        {/* Mirrors a lane's own columns, so the two edges label the track and
            not the gutters, whatever the root font size makes of the scale. */}
        <div className="flex items-center gap-2 pb-1 font-sp-mono text-[10px] text-zinc-400">
          <span className="w-14 flex-shrink-0" />
          <div className="flex flex-1 justify-between px-2">
            <span>{formatTimestamp(model.windowStart, { fallback: '—' })}</span>
            <span>{formatTimestamp(model.windowEnd, { fallback: '—' })}</span>
          </div>
          <span className="w-4 flex-shrink-0" />
        </div>

        {model.lanes.map((lane) => {
          const style = DEVICE[lane.type];

          return (
            <div key={lane.type} className="flex items-center gap-2 py-1">
              <span
                className={cn(MICRO_LABEL, 'w-14 flex-shrink-0 text-right')}
              >
                {style.label}
              </span>

              {/* The track pads itself and the axis is an ordinary box inside
                  it: a mark's percentage then measures the axis, and no height
                  is inherited down a chain of absolutely positioned parents. */}
              <div className="relative h-6 flex-1 rounded-[7px] border border-zinc-800 bg-zinc-950 px-2 py-1">
                <div className="relative h-full">
                  {lane.marks.map((mark) => {
                    const isSelected = selected?.event.id === mark.event.id;

                    return (
                      <button
                        key={mark.event.id}
                        type="button"
                        aria-pressed={isSelected}
                        aria-label={`${style.label} change at ${formatTimestamp(
                          mark.event.timestamp,
                          { fallback: 'unknown time' }
                        )}`}
                        onClick={() => setSelectedId(mark.event.id)}
                        /* Centred on its instant by half the hit area, which is
                           wider than the mark so a 4px bar stays clickable. */
                        style={{ left: `calc(${mark.offsetPercent}% - 5px)` }}
                        className="absolute top-0 h-full w-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40"
                      >
                        <span
                          className={cn(
                            'mx-auto block h-full rounded-full',
                            isSelected ? 'w-1.5 bg-zinc-100' : cn('w-1', style.mark)
                          )}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              <span className="w-4 flex-shrink-0 text-right font-sp-mono text-[10px] text-zinc-400">
                {lane.marks.length}
              </span>
            </div>
          );
        })}
      </div>

      {selected && selectedStyle && (
        <div
          className={cn(
            'rounded-[9px] border bg-zinc-950 px-3 py-2.5',
            selectedStyle.detail
          )}
        >
          <div className="flex items-center gap-2">
            <DeviceChangeBadge type={selected.type} />
            <span className="font-sp-mono text-[11px] text-zinc-200">
              {formatTimestamp(selected.event.timestamp, { fallback: '—' })}
            </span>
            <span className="font-sp-mono text-[10px] text-emerald-400">
              {formatRelativeLabel(selected.event.timestamp, startTime)}
            </span>
          </div>

          <div className="mt-2 flex items-baseline gap-2">
            <span className="w-2.5 flex-shrink-0 text-center text-[11px] text-zinc-500">
              −
            </span>
            <span
              className="min-w-0 flex-1 truncate text-[11.5px] text-zinc-400"
              title={readLabel(selected.event, 'fromLabel') ?? undefined}
            >
              {readLabel(selected.event, 'fromLabel') ?? 'None'}
            </span>
          </div>

          <div className="mt-1 flex items-baseline gap-2">
            <span className="w-2.5 flex-shrink-0 text-center text-[11px] text-zinc-500">
              →
            </span>
            <span
              className="min-w-0 flex-1 truncate text-[11.5px] font-semibold text-zinc-200"
              title={readLabel(selected.event, 'toLabel') ?? selected.event.message}
            >
              {readLabel(selected.event, 'toLabel') ?? selected.event.message}
            </span>
          </div>
        </div>
      )}

      <p className="text-[10.5px] leading-[1.5] text-zinc-400">
        Each mark is one change, placed on real elapsed time — clusters show
        where the device was flapping. Click a mark to read it.
        {model.unplacedCount > 0 &&
          ` ${model.unplacedCount} change${
            model.unplacedCount === 1 ? '' : 's'
          } named no device and sit in Log only.`}
      </p>
    </div>
  );
};
